import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Container, Grid, Card, CardContent, Typography, Stack, Chip, Divider, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Dialog, DialogContent, DialogTitle, IconButton, ImageList, ImageListItem } from '@mui/material';
import { CheckCircle as CheckCircleIcon, CloudUpload as CloudUploadIcon, Analytics as AnalyticsIcon, Dashboard as DashboardIcon, Edit as EditIcon, Schema as SchemaIcon, TableView as TableViewIcon, Visibility as VisibilityIcon, Close as CloseIcon } from '@mui/icons-material';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import theme from './theme';
import './App.css';
import { workflowData } from './data/workflowData';

// Screenshot mapping for each card
const screenshotMapping = {
  'Google Sheet Integration': [
    // '/screenshots/create_project_step_1.png',
    // '/screenshots/create_project_step_1_capcity_view.png'
  ],
  'Project Sheet Files': [
    // '/screenshots/create_project_step_1.png',
    // '/screenshots/project_dashboard_actual_data.png'
  ],
  'Project Creation': [
    '/screenshots/create_project_step_1.png',
    '/screenshots/create_project_step_1_capcity_view.png'
  ],
  'Upload Requirement Files': [
    '/screenshots/project_create_file_upload.png',
  ],
  'AI Analysis & Verification': [
    '/screenshots/create_project_step_2_data_verify.png',
    '/screenshots/create_project_step_2_data_verify_table.png'
  ],
  'Project Kick-off': [
    '/screenshots/create_project_step_3_kick_off.png'
  ],
  'Project Dashboard': [
    '/screenshots/project_dashboard.png',
    '/screenshots/project_dashboard_planned_data_view.png',
    '/screenshots/project_dashboard_actual_data.png'
  ],
  'Upload Actual Data': [
    '/screenshots/project_dashboard_upload_actual_data.png'
  ],
  'AI Analysis & Verification for Actual Data': [
    '/screenshots/create_project_step_2_data_verify_table.png'
  ],
  'Sheet File 1: Planned Data': [
    '/screenshots/create_project_step_1.png',
    '/screenshots/create_project_step_1_capcity_view.png'
  ],
  'Sheet File 2: Actual Data': [
    '/screenshots/project_dashboard_actual_data.png'
  ]
};

// Screenshot Popup Component
function ScreenshotPopup({ open, onClose, title, screenshots }) {
  if (!screenshots || screenshots.length === 0) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{title} - Screenshots</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <ImageList cols={1} gap={16}>
          {screenshots.map((screenshot, index) => (
            <ImageListItem key={index}>
              <img
                src={screenshot}
                alt={`${title} screenshot ${index + 1}`}
                loading="lazy"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
                onClick={() => window.open(screenshot, '_blank')}
              />
            </ImageListItem>
          ))}
        </ImageList>
      </DialogContent>
    </Dialog>
  );
}

// App now only composes the Workflow with data; presentational components live under components/

function SectionHeader({ stepNumber, title, chips = [] }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
      <Chip label={stepNumber} color="primary" sx={{ fontWeight: 700 }} />
      <Typography variant="h6">{title}</Typography>
      <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
        {chips.map((c, idx) => (
          <Chip key={idx} label={c} size="small" variant="outlined" />
        ))}
      </Stack>
    </Stack>
  );
}

function StepCard({ icon, title, description, children }) {
  const [openScreenshotPopup, setOpenScreenshotPopup] = useState(false);
  const screenshots = screenshotMapping[title];

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: 6,
          borderColor: 'primary.light',
        },
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          {icon}
          <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
          {screenshots && screenshots.length > 0 && (
            <IconButton 
              onClick={() => setOpenScreenshotPopup(true)} 
              size="small" 
              sx={{ ml: 'auto' }}
              title="View Screenshots"
            >
              <VisibilityIcon fontSize="small" color="primary" />
            </IconButton>
          )}
        </Stack>
        {children ? (
          children
        ) : (
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        )}
      </CardContent>
      <ScreenshotPopup
        open={openScreenshotPopup}
        onClose={() => setOpenScreenshotPopup(false)}
        title={title}
        screenshots={screenshots}
      />
    </Card>
  );
}

function Section({ color = 'primary', children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 4,
        borderLeft: (theme) => `6px solid ${theme.palette[color].main}`,
        backgroundColor: 'background.paper',
      }}
    >
      {children}
    </Paper>
  );
}

function CardsGrid({ children, sx }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
        alignItems: 'stretch',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function SheetCard({ title, sheets }) {
  const [openScreenshotPopup, setOpenScreenshotPopup] = useState(false);
  const screenshots = screenshotMapping[title];

  return (
    <Card
      variant="outlined"
      sx={{
        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: 6,
          borderColor: 'primary.light',
        },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <TableViewIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
          {screenshots && screenshots.length > 0 && (
            <IconButton 
              onClick={() => setOpenScreenshotPopup(true)} 
              size="small" 
              sx={{ ml: 'auto' }}
              title="View Screenshots"
            >
              <VisibilityIcon fontSize="small" color="primary" />
            </IconButton>
          )}
        </Stack>
        <Stack spacing={0.5} sx={{ pl: 3 }}>
          {sheets.map((s, i) => (
            <Typography key={i} variant="body2">▹ {s}</Typography>
          ))}
        </Stack>
      </CardContent>
      <ScreenshotPopup
        open={openScreenshotPopup}
        onClose={() => setOpenScreenshotPopup(false)}
        title={title}
        screenshots={screenshots}
      />
    </Card>
  );
}

function PlannedVsActualChart({ data }) {
  const [openScreenshotPopup, setOpenScreenshotPopup] = useState(false);
  const screenshots = screenshotMapping['Project Dashboard'];

  const base = [
    { name: 'Labor', planned: 80, actual: 65 },
    { name: 'Cost', planned: 60, actual: 75 },
    { name: 'Boards', planned: 45, actual: 40 },
  ];

  const chartData = base.map((d) => ({
    name: d.name,
    Planned: d.planned,
    Actual: d.actual,
    VariancePct: Number((((d.actual - d.planned) / d.planned) * 100).toFixed(1)),
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const planned = payload.find((p) => p.dataKey === 'Planned')?.value ?? 0;
    const actual = payload.find((p) => p.dataKey === 'Actual')?.value ?? 0;
    const variance = ((actual - planned) / (planned || 1)) * 100;
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', padding: 10, borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
        <div style={{ fontSize: 12, color: '#374151' }}>Planned: {planned}</div>
        <div style={{ fontSize: 12, color: '#374151' }}>Actual: {actual}</div>
        <div style={{ fontSize: 12, color: variance >= 0 ? '#059669' : '#b91c1c' }}>
          Variance: {variance >= 0 ? '+' : ''}{variance.toFixed(1)}%
        </div>
      </div>
    );
  };

  return (
    <Card
      variant="outlined"
      sx={{
        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: 6,
          borderColor: 'primary.light',
        },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <AnalyticsIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>Project Dashboard: Planned vs Actual (Example)</Typography>
          {screenshots && screenshots.length > 0 && (
            <IconButton 
              onClick={() => setOpenScreenshotPopup(true)} 
              size="small" 
              sx={{ ml: 'auto' }}
              title="View Screenshots"
            >
              <VisibilityIcon fontSize="small" color="primary" />
            </IconButton>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          eg: Budget vs Actual, Insights, Future forecast etc.
        </Typography>
        <Box sx={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <defs>
                <linearGradient id="gradPlanned" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.65} />
                </linearGradient>
                <linearGradient id="gradActual" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.6} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tickLine={false} />
              <YAxis yAxisId="left" tickLine={false} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={(v)=>`${v}%`} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar yAxisId="left" dataKey="Planned" fill="url(#gradPlanned)" radius={[8,8,0,0]} barSize={26} />
              <Bar yAxisId="left" dataKey="Actual" fill="url(#gradActual)" radius={[8,8,0,0]} barSize={26} />
              <Line yAxisId="right" type="monotone" dataKey="VariancePct" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} name="Variance %" />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
      <ScreenshotPopup
        open={openScreenshotPopup}
        onClose={() => setOpenScreenshotPopup(false)}
        title="Project Dashboard"
        screenshots={screenshots}
      />
    </Card>
  );
}

function WorkflowPage() {
  const { configuration, phases, approvals, additional } = workflowData;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" textAlign="center" sx={{ mb: 4 }}>
        Tymeline AMD Project Management Workflow
      </Typography>

        {/* Configuration */}
        <Section color="primary">
          <SectionHeader stepNumber={0} title="Configuration (Integration setup)" chips={["Org-wide", "Google Sheets"]} />
          <CardsGrid>
            <Box>
              <StepCard icon={<SchemaIcon fontSize="small" color="primary" />} title={configuration.title} description={configuration.description} />
            </Box>
            <Box>
              <StepCard icon={<TableViewIcon fontSize="small" color="primary" />} title="Project Sheet Files">
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Each project uses two Google Sheet files. This keeps planned and actuals cleanly separated.
                  </Typography>
                  <List dense sx={{ pt: 0 }}>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <TableViewIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography variant="body2" fontWeight={600}>Planned Data</Typography>}
                        secondary={<Typography variant="body2" color="text.secondary">Labor requirements, labor cost, and additional costs (boards, lab, chip)</Typography>}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <TableViewIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography variant="body2" fontWeight={600}>Actual Data</Typography>}
                        secondary={<Typography variant="body2" color="text.secondary">Approved actuals for labor and additional costs captured during execution</Typography>}
                      />
                    </ListItem>
                  </List>
                </Stack>
              </StepCard>
            </Box>
          </CardsGrid>
        </Section>

        {/* Phase 1 */}
        <Section color="secondary">
          <SectionHeader stepNumber={1} title="Initial Setup & Planning Phase" />
          <CardsGrid sx={{ mb: 2 }}>
            <Box>
              <StepCard icon={<TableViewIcon fontSize="small" color="primary" />} title={phases[0].steps[0].title} description={phases[0].steps[0].details} />
            </Box>
            <Box>
              <StepCard icon={<CloudUploadIcon fontSize="small" color="primary" />} title={phases[0].steps[1].title} description={phases[0].steps[1].details} />
            </Box>
            <Box>
              <StepCard icon={<EditIcon fontSize="small" color="primary" />} title={phases[0].steps[2].title} description={phases[0].steps[2].details} />
            </Box>
            <Box>
              <StepCard icon={<CheckCircleIcon fontSize="small" color="primary" />} title={phases[0].steps[3].title} description={phases[0].steps[3].details} />
            </Box>
          </CardsGrid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SheetCard title={phases[0].sheets.title} sheets={phases[0].sheets.items} />
            </Grid>
          </Grid>
        </Section>

        {/* Phase 2 */}
        <Section color="primary">
          <SectionHeader stepNumber={2} title="Project Execution Phase" />
          <CardsGrid sx={{ mb: 2 }}>
            <Box>
              <StepCard icon={<DashboardIcon fontSize="small" color="primary" />} title={phases[1].steps[0].title} description={phases[1].steps[0].details} />
            </Box>
            <Box>
              <StepCard icon={<CheckCircleIcon fontSize="small" color="primary" />} title={phases[1].steps[1].title} description={phases[1].steps[1].details} />
            </Box>
            <Box>
              <StepCard icon={<CloudUploadIcon fontSize="small" color="primary" />} title={phases[1].steps[2].title} description={phases[1].steps[2].details} />
            </Box>
            <Box>
              <StepCard icon={<EditIcon fontSize="small" color="primary" />} title={phases[1].steps[3].title} description={phases[1].steps[3].details} />
            </Box>
            <Box>
              <StepCard icon={<CheckCircleIcon fontSize="small" color="primary" />} title={phases[1].steps[4].title} description={phases[1].steps[4].details} />
            </Box>
          </CardsGrid>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <PlannedVsActualChart />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SheetCard title={phases[1].sheets.title} sheets={phases[1].sheets.items} />
            </Grid>
          </Grid>
        </Section>

        {/* Approvals */}
        <Section color="secondary">
          <SectionHeader stepNumber={3} title="Approval Flows" />
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: 6,
                    borderColor: 'success.light',
                  },
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} color="success.main">Current Approval Flow</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Stack spacing={1} sx={{ pl: 2 }}>
                    {approvals.current.map((s, i) => (
                      <Typography key={i} variant="body2">• {s}</Typography>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: 6,
                    borderColor: 'primary.light',
                  },
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} color="primary.main">Future Approval Flow</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Stack spacing={1} sx={{ pl: 2 }}>
                    {approvals.future.map((s, i) => (
                      <Typography key={i} variant="body2">• {s}</Typography>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Section>

        {/* Additional */}
        <Section color="primary">
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Additional</Typography>
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1} sx={{ pl: 2 }}>
                {additional.map((s, i) => (
                  <Typography key={i} variant="body2">• {s}</Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Section>
    </Container>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route path="/" element={<Navigate to="/flows" replace />} />
        <Route path="/flows" element={React.createElement(require('./pages/Home').default)} />
        <Route path="/availability" element={React.createElement(require('./pages/Availability').default)} />
        <Route path="/workflows/amd" element={<WorkflowPage />} />
        <Route path="/workflows/voice-chat" element={React.createElement(require('./pages/VoiceChatFlow').default)} />
        <Route path="/workflows/voice-chat-rest" element={React.createElement(require('./pages/RestVoiceChatFlow').default)} />
        <Route path="/workflows/ai-employee" element={React.createElement(require('./pages/AIEmployeeWorkflow').default)} />
        <Route path="/workflows/ai-employee-diagrams" element={React.createElement(require('./pages/AIEmployeeProductDiagram').default)} />
        <Route path="*" element={<Navigate to="/flows" replace />} />
      </Routes>
    </ThemeProvider>
  );
}
